import React, { useEffect, useState, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CoverPhoto, IEvent } from '../../store/events/interfaces'

export const getEventPhotos = (event: IEvent | undefined, enablePreviewImages: boolean): CoverPhoto[] => {
  const allPhotos: CoverPhoto[] = []
  if (event?.cover_photo && enablePreviewImages) {
    allPhotos.push(event.cover_photo)
  }
  if (event?.photos) {
    event.photos.forEach((photo) => {
      const isDuplicate = allPhotos.some(ap => ap.medium === photo.medium || ap.thumbnail === photo.thumbnail)
      if (!isDuplicate) {
        allPhotos.push(photo)
      }
    })
  }
  return allPhotos
}

export const useLightbox = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [images, setImages] = useState<CoverPhoto[]>([])

  const openLightbox = useCallback((newImages: CoverPhoto[], index: number = 0) => {
    setImages(newImages)
    setCurrentIndex(index)
    setIsOpen(true)
  }, [])

  const closeLightbox = useCallback(() => {
    setIsOpen(false)
  }, [])

  return {
    isOpen,
    currentIndex,
    images,
    openLightbox,
    closeLightbox,
    lightboxProps: {
      images,
      initialIndex: currentIndex,
      isOpen,
      onClose: closeLightbox,
    }
  }
}

interface LightboxProps {
  images: CoverPhoto[]
  initialIndex: number
  isOpen: boolean
  onClose: () => void
}

export const Lightbox: React.FC<LightboxProps> = ({
  images,
  initialIndex,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [touchStartX, setTouchStartX] = useState<number | null>(null)

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex)
      // Prevent body scrolling when lightbox is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, initialIndex])

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }, [images.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }, [images.length])

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowLeft') {
        goToPrev()
      } else if (e.key === 'ArrowRight') {
        goToNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isOpen, onClose, goToPrev, goToNext])

  // Swipe handlers for mobile touch support
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return
    const touchEndX = e.changedTouches[0].clientX
    const diff = touchStartX - touchEndX
    
    // Swipe left (next) or right (prev)
    if (diff > 50) {
      goToNext()
    } else if (diff < -50) {
      goToPrev()
    }
    setTouchStartX(null)
  }

  if (!isOpen || images.length === 0) return null

  const activeImage = images[currentIndex]

  return (
    <div 
      className="custom-lightbox-overlay"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button 
        className="lightbox-close-btn"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Schließen"
      >
        <FontAwesomeIcon icon="times" size="2x" />
      </button>

      {/* Navigation arrows (only if multiple images) */}
      {images.length > 1 && (
        <>
          <button 
            className="lightbox-nav-btn prev"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            aria-label="Vorheriges Bild"
          >
            <FontAwesomeIcon icon="chevron-left" size="2x" />
          </button>
          
          <button 
            className="lightbox-nav-btn next"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="Nächstes Bild"
          >
            <FontAwesomeIcon icon="chevron-right" size="2x" />
          </button>
        </>
      )}

      {/* Main Image Container */}
      <div className="lightbox-content-wrapper" onClick={(e) => e.stopPropagation()}>
        <img 
          src={activeImage.medium || activeImage.thumbnail} 
          alt={activeImage.caption || `Bild ${currentIndex + 1}`} 
          className="lightbox-main-img"
        />

        {/* Counter and Caption info */}
        <div className="lightbox-info-panel">
          {images.length > 1 && (
            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>
          )}
          
          {activeImage.caption && (
            <p className="lightbox-caption">{activeImage.caption}</p>
          )}

          {(activeImage.credits || activeImage.source) && (
            <div className="lightbox-credits-source">
              {activeImage.credits && (
                <span className="mr-3">Urheber: {activeImage.credits}</span>
              )}
              {activeImage.source && (
                <span>
                  Quelle:{' '}
                  <a 
                    href={activeImage.source} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="lightbox-source-link"
                  >
                    Link
                  </a>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
